'use client'

import { preload } from 'react-dom'
import { getImageProps } from 'next/image'
import Link from 'next/link'

import Carousel from '@/core/components/optional/carousel'
import Container from '@/features/site/components/container'

import type { Route } from 'next'

export type HeroSlide = {
  id: string
  href: Route
  image: string
  mobileImage: string
  imageAlt: string
}

function HeroImage({ slide, eager }: { slide: HeroSlide; eager: boolean }) {
  const shared = {
    alt: slide.imageAlt,
    fill: true,
    priority: eager,
  }
  const {
    props: { src: desktopSrc, srcSet: desktopSrcSet, sizes: desktopSizes },
  } = getImageProps({
    ...shared,
    src: slide.image,
    sizes: '(min-width: 1001px) 1232px, 100vw',
  })
  const { props: mobileProps } = getImageProps({
    ...shared,
    src: slide.mobileImage,
    sizes: '100vw',
  })
  if (eager) {
    preload(mobileProps.src, {
      as: 'image',
      fetchPriority: 'high',
      imageSrcSet: mobileProps.srcSet,
      imageSizes: mobileProps.sizes,
      media: '(max-width: 800px)',
    })
    if (desktopSrc) {
      preload(desktopSrc, {
        as: 'image',
        fetchPriority: 'high',
        imageSrcSet: desktopSrcSet,
        imageSizes: desktopSizes,
        media: '(min-width: 801px)',
      })
    }
  }
  return (
    <picture>
      <source
        media="(min-width: 801px)"
        srcSet={desktopSrcSet}
        sizes={desktopSizes}
      />
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img {...mobileProps} className="object-cover" />
    </picture>
  )
}

export default function HomeHero({ slides }: { slides: HeroSlide[] }) {
  return (
    <Container className="pt-4 over10:pt-6">
      <Carousel
        ariaLabel="주요 안내"
        loop
        autoplayMs={6000}
        classNames={{
          viewport: 'rounded-panel over10:mx-16',
          arrow:
            'hidden size-12 rounded-full border border-border bg-background hover:bg-surface-soft over10:flex',
          arrowPrev: 'left-0 top-1/2 -translate-y-1/2',
          arrowNext: 'right-0 top-1/2 -translate-y-1/2',
          dots: 'mt-2',
        }}
        slides={slides.map((slide, index) => ({
          id: slide.id,
          content: (
            <Link
              href={slide.href}
              className="relative block aspect-[5/4] overflow-hidden bg-surface-soft over8:aspect-[24/5]"
            >
              <HeroImage slide={slide} eager={index === 0} />
            </Link>
          ),
        }))}
      />
    </Container>
  )
}
